import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import CarteOutremer from "@/components/CarteOutremer";
import { Loader2, MapPin, Building2, Users, Briefcase, Clock, FileSignature, Receipt, TrendingUp } from "lucide-react";

// Palette couleur par territoire (déterministe)
const TERR_META = {
  "Guadeloupe": { emoji: "🇬🇵", color: "#C5A059" },
  "Martinique": { emoji: "🇲🇶", color: "#5B8DEF" },
  "Guyane": { emoji: "🇬🇫", color: "#43A974" },
  "La Réunion": { emoji: "🇷🇪", color: "#EF4444" },
  "Mayotte": { emoji: "🇾🇹", color: "#7B6FBF" },
  "Saint-Martin": { emoji: "🇲🇫", color: "#F59E0B" },
  "Saint-Pierre-et-Miquelon": { emoji: "🇵🇲", color: "#0EA5E9" },
  "Polynésie française": { emoji: "🇵🇫", color: "#EC4899" },
  "Nouvelle-Calédonie": { emoji: "🇳🇨", color: "#14B8A6" },
};

const fmt = (n) => Number(n || 0).toLocaleString("fr-FR");

export default function TerritoiresPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const cardsRef = useRef(null);

  useEffect(() => {
    api.get("/kpi/territoires")
      .then(({ data }) => setData(data))
      .catch((err) => setError(err.response?.data?.detail || "Accès réservé"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-[#C5A059]" /></div>;
  }
  if (error) return null; // silencieusement masqué pour les rôles non autorisés
  const terrs = data?.territoires || [];
  if (terrs.length === 0) {
    return <div className="text-sm text-slate-500 italic">Aucun territoire actif pour le moment.</div>;
  }

  const totals = terrs.reduce((acc, t) => ({
    entreprises: acc.entreprises + t.entreprises,
    salaries: acc.salaries + t.salaries,
    missions: acc.missions + t.missions_en_cours,
    heures: acc.heures + t.heures_mois,
    emis: acc.emis + t.montant_emis,
    du: acc.du + t.montant_du,
  }), { entreprises: 0, salaries: 0, missions: 0, heures: 0, emis: 0, du: 0 });

  const maxHeures = Math.max(...terrs.map(t => t.heures_mois), 1);

  const handleSelect = (name) => {
    setSelected(selected === name ? null : name);
    // Scroll vers la carte correspondante
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="territoire-card-${name.replace(/\s/g, "-")}"]`);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  };

  return (
    <div className="space-y-5" data-testid="territoires-panel">
      {/* Carte Outre-mer */}
      <CarteOutremer territoires={terrs} onSelect={handleSelect} selected={selected} />

      {/* Summary strip */}
      <div className="rounded-xl border border-slate-200 bg-gradient-to-r from-[#FDF9F1] to-white p-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <SummaryStat icon={MapPin} label="Territoires actifs" value={terrs.length} />
        <SummaryStat icon={Building2} label="Entreprises" value={fmt(totals.entreprises)} />
        <SummaryStat icon={Users} label="Salariés" value={fmt(totals.salaries)} />
        <SummaryStat icon={Briefcase} label="Missions actives" value={fmt(totals.missions)} />
        <SummaryStat icon={Clock} label="Heures du mois" value={`${fmt(totals.heures.toFixed(1))}h`} />
        <SummaryStat icon={Receipt} label="Solde dû" value={`${fmt(totals.du)} €`} />
      </div>

      {/* Grille cartes territoires */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {terrs.map((t) => {
          const meta = TERR_META[t.territoire] || { emoji: "🌴", color: "#64748B" };
          const barWidth = Math.max(2, (t.heures_mois / maxHeures) * 100);
          return (
            <div
              key={t.territoire}
              data-testid={`territoire-card-${t.territoire.replace(/\s/g, "-")}`}
              className={`bg-white border rounded-xl overflow-hidden transition-all ${selected === t.territoire ? "border-slate-900 shadow-lg scale-[1.02]" : "border-slate-200 hover:shadow-md"}`}
            >
              <div className="h-1" style={{ backgroundColor: meta.color }} />
              <div className="p-5">
                <div className="flex items-center gap-2">
                  <span className="text-2xl" aria-hidden="true">{meta.emoji}</span>
                  <h3 className="font-semibold text-slate-900 text-sm truncate" style={{ fontFamily: "Outfit, sans-serif" }}>
                    {t.territoire}
                  </h3>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                  <StatMini icon={Building2} label="Entreprises" value={t.entreprises} />
                  <StatMini icon={Users} label="Salariés" value={t.salaries} />
                  <StatMini icon={FileSignature} label="Conventions" value={t.conventions} />
                  <StatMini icon={Briefcase} label="Missions" value={t.missions_en_cours} highlight={t.missions_en_cours > 0} />
                </div>

                {/* Heures bar */}
                <div className="mt-4">
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-slate-500">Heures ({data.month})</span>
                    <span className="font-semibold text-slate-900">{t.heures_mois.toFixed(1)}h</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${barWidth}%`, backgroundColor: meta.color }} />
                  </div>
                  <div className="mt-1 flex items-center gap-1 text-[10px]">
                    <TrendingUp className="h-3 w-3 text-emerald-500" />
                    <span className="text-slate-500">Validé: {t.heures_validees.toFixed(1)}h · {t.taux_validation}%</span>
                  </div>
                </div>

                {/* Finance */}
                <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-3 gap-2 text-center">
                  <div>
                    <div className="text-[9px] uppercase text-slate-400">Émis</div>
                    <div className="text-xs font-semibold text-slate-900">{fmt(t.montant_emis)}€</div>
                  </div>
                  <div>
                    <div className="text-[9px] uppercase text-slate-400">Payé</div>
                    <div className="text-xs font-semibold text-emerald-700">{fmt(t.montant_paye)}€</div>
                  </div>
                  <div>
                    <div className="text-[9px] uppercase text-slate-400">Dû</div>
                    <div className={`text-xs font-semibold ${t.montant_du > 0 ? "text-red-600" : "text-slate-600"}`}>{fmt(t.montant_du)}€</div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SummaryStat({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3">
      <div className="h-9 w-9 rounded-lg bg-white border border-[#C5A059]/30 flex items-center justify-center text-[#C5A059]">
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-slate-400 truncate">{label}</div>
        <div className="text-sm font-semibold text-slate-900 truncate">{value}</div>
      </div>
    </div>
  );
}

function StatMini({ icon: Icon, label, value, highlight = false }) {
  return (
    <div className={`flex items-center gap-2 p-2 rounded-lg ${highlight ? "bg-[#FDF9F1]" : "bg-slate-50"}`}>
      <Icon className={`h-3.5 w-3.5 ${highlight ? "text-[#C5A059]" : "text-slate-400"}`} />
      <div className="min-w-0">
        <div className="text-[9px] uppercase text-slate-400">{label}</div>
        <div className={`text-sm font-semibold ${highlight ? "text-[#A88846]" : "text-slate-900"}`}>{value}</div>
      </div>
    </div>
  );
}
